// @ts-check
/** @jsx jsx */
import {
  Box,
  Button,
  Container,
  Flex,
  P,
  Title,
} from '@bottlebooks/gatsby-theme-base';
import { Trans } from '@lingui/macro';
import { jsx } from 'theme-ui';
import useLink from '../../useLink';
import Layout from '../Layout';
import ExhibitorListRowSmall from '../SiteSearch/ExhibitorListRowSmall';
import ProductListRowSmall from '../SiteSearch/ProductListRowSmall';
import useEventData from '../SiteSearch/useEventData';
import ToggleFavorite from './ToggleFavorite';
import useFavorites from './useFavorites';

export default function FavoritesPage({ locale }) {
  const { favorites } = useFavorites();
  const data = useEventData();

  return (
    <Layout locale={locale}>
      <Container>
        {!favorites.length ? (
          <EmptyState />
        ) : (
          <Box>
            <Title variant="large" sx={{ marginTop: 5, marginBottom: 4 }}>
              <Trans>Your Favorites</Trans>
            </Title>
            {favorites.map((favorite) => (
              <FavoriteItem
                key={favorite.id}
                favorite={addData(favorite, data)}
              />
            ))}
          </Box>
        )}
      </Container>
    </Layout>
  );
}

function EmptyState(rest) {
  const link = useLink();
  return (
    <Box sx={{ textAlign: 'center' }} {...rest}>
      <Title sx={{ marginTop: 5, marginBottom: 4 }}>
        <Trans>No products saved.</Trans>
      </Title>
      <P variant="large" sx={{ margin: 'auto' }}>
        <Trans>
          You haven't saved any products to taste. Start your favorites list by
          saving some products.
        </Trans>
      </P>
      <Button
        variant="primary"
        to={link.products()}
        sx={{ paddingX: 4, paddingY: 2, marginTop: 4 }}
      >
        <Trans>Explore all products</Trans>
      </Button>
    </Box>
  );
}

/**
 *
 * @param {object} props
 * @param {import('./useFavorites').Favorite} props.favorite
 * @returns
 */
function FavoriteItem({ favorite, ...rest }) {
  const { toggleFavorite } = useFavorites();
  const link = useLink();
  function getLink(favorite) {
    if (favorite.type === 'exhibitor') {
      return link.exhibitor({ exhibitorId: favorite.id });
    }
    if (favorite.type === 'product') {
      return link.product({
        productId: favorite.id,
        exhibitorId: favorite.exhibitorId,
      });
    }
    if (favorite.type === 'producer') {
      return link.producer({ producerId: favorite.id });
    }
    return '';
  }

  if (favorite.type === 'product') {
    return (
      <Flex>
        <ProductListRowSmall
          product={favorite}
          to={getLink(favorite)}
          searchTerm=""
          sx={{ flex: 1, marginRight: 3 }}
        />
        <ToggleFavorite
          isToggled
          onClick={() => toggleFavorite(favorite.id)}
          variant="small"
        />
      </Flex>
    );
  }
  if (favorite.type === 'exhibitor') {
    return (
      <Flex>
        <ExhibitorListRowSmall
          exhibitor={favorite}
          to={getLink(favorite)}
          searchTerm=""
          sx={{ flex: 1, marginRight: 3 }}
        />
        <ToggleFavorite
          isToggled
          onClick={() => toggleFavorite(favorite.id)}
          variant="small"
        />
      </Flex>
    );
  }
  return <Box {...rest}>{favorite.name}</Box>;
}

/**
 *
 * @param {import('./useFavorites').Favorite} favorite
 * @param {{products:{ productId: string }[],exhibitors:{ brandId: string }[]}} allData
 */
function addData(favorite, { exhibitors, products }) {
  if (favorite.type === 'product') {
    return {
      ...favorite,
      ...products.find((product) => product.productId === favorite.id),
    };
  }
  if (favorite.type === 'exhibitor') {
    return {
      ...favorite,
      ...exhibitors.find((exhibitors) => exhibitors.brandId === favorite.id),
    };
  }
  return favorite;
}
